// format.ts — single source of truth for currency and date/time rendering
// across the frontend. Closes #1033: before this file, the same ~5 shapes
// of formatter had been re-implemented ~18 times (three different `fmt$`,
// two different `formatCurrencyFull`, seven different `formatTime`/date
// helpers) and had already drifted — see PR body for the full audit.
//
// Design note on `treatZeroAsEmpty`: several DB-sourced money fields
// (assessedValue, amountDue) use a literal 0 to mean "we don't have this
// data," while computed values (deal-analyzer cash flow, bid amounts) can
// be legitimately zero and must render "$0", not a fallback string. Each
// call site says which camp it's in instead of the historical bare
// `!value` check silently conflating the two.

interface CurrencyOptions {
  /** Text rendered when the value is missing. Default "—". */
  fallback?: string;
  /**
   * Treat 0 (and negative — no money field in this app is ever
   * legitimately negative, so the distinction doesn't matter in practice)
   * as "missing" too, not just null/undefined. Default false: 0 renders
   * as "$0".
   */
  treatZeroAsEmpty?: boolean;
}

/**
 * Full USD currency, no cents (e.g. "$245,000"). `value` may be
 * null/undefined for DB fields that aren't always populated; pass
 * `treatZeroAsEmpty: true` for fields where a stored 0 also means
 * "unknown" (assessed value, amount due) rather than a real zero.
 */
export function formatCurrency(
  value: number | null | undefined,
  options: CurrencyOptions = {}
): string {
  const { fallback = "—", treatZeroAsEmpty = false } = options;
  if (value === null || value === undefined) return fallback;
  if (treatZeroAsEmpty && value <= 0) return fallback;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Abbreviated USD currency ("$1.2M" / "$450K" / "$75"). `value` may be
 * null/undefined/0/NaN — all render `fallback` (default "$0").
 */
export function formatCurrencyShort(
  value: number | null | undefined,
  fallback: string = "$0"
): string {
  if (!value) return fallback;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${Math.round(value / 1_000)}K`;
  return `$${Math.round(value)}`;
}

// #1688: an ISO string ending in exact UTC midnight ("...T00:00:00Z" or
// "...T00:00:00.000Z") is the shape Go's RFC3339 marshaling ALWAYS produces
// for a Postgres `DATE` column (auction_date, sale dates, ...) — a DATE
// column has no time-of-day at all, but Go's time.Time type has no
// "dateless" representation, so it always lands on midnight UTC.
// `new Date(iso).toLocaleDateString(...)` corrupts these: it parses the
// instant correctly, then renders in the VIEWER's local timezone, and
// every US timezone has a negative UTC offset — so UTC midnight rolls back
// to the PREVIOUS calendar day locally. Confirmed live: 100% consistent
// across every sampled auction_date.
const UTC_MIDNIGHT_RE = /T00:00:00(\.0+)?Z$/;

/**
 * Parses a date string for CALENDAR-DATE display (a date with no
 * meaningful time-of-day). When the input is exact UTC midnight, parses
 * the Y-M-D components directly and builds the `Date` via the
 * LOCAL-timezone constructor (`new Date(y, m-1, d)`) — that constructor
 * never parses a string at all, so there is no timezone to misapply. Any
 * other shape (a genuine timestamp) falls through to normal `Date`
 * parsing unchanged: those really do represent an instant, and converting
 * to the viewer's local time for display is correct, not a bug.
 *
 * Tradeoff: a genuine timestamp that happens to land on exact UTC
 * midnight (~1-in-86400 per value, e.g. `BidTimeline.tsx`'s `eventDate`/
 * `recordedAt`) is indistinguishable from a DATE column here and gets the
 * same treatment — displayed on its UTC calendar day rather than
 * converted to local time. That's a bounded, rare, boundary-ambiguous
 * edge case (which calendar day a midnight-UTC instant "belongs to" is
 * arguably unclear either way), accepted in exchange for not having to
 * thread a type marker through the API to distinguish DATE from
 * TIMESTAMP columns for this fix.
 */
function parseForCalendarDisplay(iso: string): Date {
  if (UTC_MIDNIGHT_RE.test(iso)) {
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const [, y, mo, d] = m;
      return new Date(Number(y), Number(mo) - 1, Number(d));
    }
  }
  return new Date(iso);
}

/**
 * Long-form date ("January 5, 2026"). `dateStr` may be null/undefined;
 * renders `fallback` (default "—") when missing.
 */
export function formatDate(
  dateStr: string | null | undefined,
  fallback: string = "—"
): string {
  if (!dateStr) return fallback;
  return parseForCalendarDisplay(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Short-form date ("Jan 5, 2026", or "Jan 05, 2026" with `padDay: true`).
 * Falls back to the raw input string when it doesn't parse as a valid
 * date, rather than rendering the literal "Invalid Date".
 */
export function formatShortDate(iso: string, options: { padDay?: boolean } = {}): string {
  const { padDay = false } = options;
  const d = parseForCalendarDisplay(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: padDay ? "2-digit" : "numeric",
    year: "numeric",
  });
}

/**
 * "Added" date for a property row (`createdAt`, i.e. when Parcely first
 * ingested it — not the court filing date). Same rendering as
 * `formatShortDate`, but returns "" instead of echoing the input when it is
 * missing or unparseable, so callers can `&&`-guard it: fixtures and rows
 * without a timestamp render nothing rather than "Added Invalid Date".
 */
export function formatAddedDate(
  iso: string | null | undefined,
  options: { padDay?: boolean } = {}
): string {
  if (!iso) return "";
  const shown = formatShortDate(iso, options);
  return shown === iso ? "" : shown;
}

/**
 * Short date + time ("Jan 5, 3:45 PM"). `iso` may be null/undefined;
 * renders `fallback` (default "—") when missing. Falls back to the raw
 * input string if `Date`/`toLocaleString` throws.
 */
export function formatDateTime(iso: string | null | undefined, fallback: string = "—"): string {
  if (!iso) return fallback;
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
